import { useEffect, useMemo, useState } from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import useIntelligentPricingReview from "../../data/useIntelligentPricingReview";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import useSettings from "../../data/useSettings";
import { IPricingMember, PricingAlignTarget, PricingMemberKind } from "../../models/pricing/IPricing";
import { IEbaySearchPartFields } from "../ebayListings/unlistedTable/UnlistedEbaySearchButtons";
import {
  applyGradeMarkdown,
  DEFAULT_GRADE_B_MARKDOWN_PERCENT,
  DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT,
  GRADE_B_MARKDOWN_KEY,
  GRADE_C_ONWARDS_MARKDOWN_KEY,
  hasGrade,
  parseGradeMarkdownPercent,
} from "../../utils/gradePricing";
import formatDateTime from "../../utils/formatDateTime";
import { MAX_PART_PRICE } from "../../utils/priceLimits";
import PriceFormat from "../../utils/priceFormat";
import { daysSince, previewAlignPrice } from "../../utils/pricingGroups";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import IntelligenceReviewModal from "../ebayOptimisation/IntelligenceReviewModal";
import UnlistedEbaySearchButtons from "../ebayListings/unlistedTable/UnlistedEbaySearchButtons";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingPulse from "../shared/LoadingPulse";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import PricingPartProButton from "./PricingPartProButton";

interface IPricingMembersProps {
  members: IPricingMember[] | undefined;
  isLoading: boolean;
  isAligning: boolean;
  onAlign: (partIds: string[], target: PricingAlignTarget, customPrice?: number | null) => void;
}

type ConfirmKind = "align" | "intelligent" | "markdown" | "lastSold" | "lastSoldMarkdown";

const segmentClasses = (active: boolean) =>
  classNames(
    "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors",
    active ? "bg-primary-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
  );

const formatDays = (iso: string | null | undefined) => {
  const days = daysSince(iso);
  return days == null ? "—" : String(days);
};

const partNumberLines = (row: IPricingMember) =>
  [
    row.partNumber ? { label: "MPN", value: row.partNumber } : null,
    row.oePartNumber ? { label: "OEM", value: row.oePartNumber } : null,
    row.otherPartNumbers ? { label: "Other", value: row.otherPartNumbers } : null,
  ].filter((line): line is { label: string; value: string } => line != null);

const ebaySearchPartFromMember = (row: IPricingMember): IEbaySearchPartFields => ({
  partNumber: row.partNumber,
  oePartNumber: row.oePartNumber,
  vehicle: {
    dvlaYearOfManufacture: row.vehicleYear,
    combinedMake: row.vehicleMake,
    modelRangeDescription: row.vehicleModel,
  },
  category: { name: row.categoryName },
});

/**
 * The parts in the selected pricing group, with checkboxes and a bar to align their prices.
 */
export default function PricingMembers({ members, isLoading, isAligning, onAlign }: IPricingMembersProps) {
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const { intelligencePricingBulk } = usePartsLookupMutations("");
  const { generalSettings } = useSettings();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [target, setTarget] = useState<PricingAlignTarget>(PricingAlignTarget.Lowest);
  const [customPrice, setCustomPrice] = useState<string>("");
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [reviewPartId, setReviewPartId] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const { latestReview } = useIntelligentPricingReview(reviewPartId ?? undefined, "PricingMembers");

  const gradeB = parseGradeMarkdownPercent(
    generalSettings.find((setting) => setting.key === GRADE_B_MARKDOWN_KEY)?.value,
    DEFAULT_GRADE_B_MARKDOWN_PERCENT
  );
  const gradeC = parseGradeMarkdownPercent(
    generalSettings.find((setting) => setting.key === GRADE_C_ONWARDS_MARKDOWN_KEY)?.value,
    DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT
  );

  const rows = members ?? [];
  const selected = rows.filter((row) => selectedIds.includes(row.partId));
  const parsedCustom = customPrice === "" ? null : Number(customPrice);
  const preview = previewAlignPrice(
    selected.map((row) => row.currentPrice),
    target,
    Number.isFinite(parsedCustom as number) ? parsedCustom : null
  );
  const listedCount = selected.filter((row) => row.kind === PricingMemberKind.Listed).length;
  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const intelReady = selected.filter((row) => row.intelligenceAverage != null && row.intelligenceAverage > 0);
  const markdownReady = intelReady.filter((row) => hasGrade(row.grade));
  const lastSoldReady = selected.filter((row) => row.lastSoldPrice != null && row.lastSoldPrice > 0);
  const lastSoldMarkdownReady = lastSoldReady.filter((row) => hasGrade(row.grade));

  const vehicleLabel = (row: IPricingMember) => {
    const parts = [row.vehicleMake, row.vehicleModel, row.vehicleYear ? String(row.vehicleYear) : null].filter(Boolean);
    return parts.join(" ") || "—";
  };

  const toggle = (partId: string) => {
    setSelectedIds((current) => (current.includes(partId) ? current.filter((id) => id !== partId) : [...current, partId]));
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : rows.map((row) => row.partId));
  };

  const selectedKey = useMemo(() => rows.map((row) => row.partId).join("|"), [rows]);
  useEffect(() => {
    const ids = new Set((members ?? []).map((row) => row.partId));
    setSelectedIds((current) => current.filter((id) => ids.has(id)));
  }, [selectedKey, members]);

  const canApply =
    selected.length > 0 &&
    preview != null &&
    preview > 0 &&
    preview <= MAX_PART_PRICE &&
    (target !== PricingAlignTarget.Custom || (parsedCustom != null && parsedCustom > 0));

  const liveListingNote = (count: number) =>
    count > 0
      ? `${count} live eBay listing${count === 1 ? "" : "s"} will update immediately. Unlisted stock only changes the entered price.`
      : "Unlisted stock only changes the entered price — nothing is listed on eBay.";

  const confirmCopy = () => {
    if (confirmKind === "intelligent") {
      return {
        title: "Set these to their intelligent price?",
        message: `This will set each of the ${selected.length} selected part${selected.length === 1 ? "" : "s"} to its own intelligent price. Parts without a lookup are skipped. ${liveListingNote(listedCount)}`,
        confirmButtonText: "Set intelligent prices",
      };
    }

    if (confirmKind === "markdown") {
      return {
        title: "Apply grade markdowns?",
        message: `This will mark each of the ${selected.length} selected part${selected.length === 1 ? "" : "s"} down from its intelligent price by the grade setting (B ${gradeB}%, C onwards ${gradeC}%). Grade A stays at the intelligent price. Parts without a grade or lookup are skipped. ${liveListingNote(listedCount)}`,
        confirmButtonText: "Apply grade markdowns",
      };
    }

    if (confirmKind === "lastSold") {
      return {
        title: "Set these to last sold?",
        message: `This will set each of the ${selected.length} selected part${selected.length === 1 ? "" : "s"} to its last sold price. Parts without a last sold figure are skipped. ${liveListingNote(listedCount)}`,
        confirmButtonText: "Set last sold prices",
      };
    }

    if (confirmKind === "lastSoldMarkdown") {
      return {
        title: "Apply last sold grade markdowns?",
        message: `This will mark each of the ${selected.length} selected part${selected.length === 1 ? "" : "s"} down from its last sold price by the grade setting (B ${gradeB}%, C onwards ${gradeC}%). Grade A stays at last sold. Parts without a grade or last sold figure are skipped. ${liveListingNote(listedCount)}`,
        confirmButtonText: "Apply last sold markdowns",
      };
    }

    return {
      title: "Align these prices?",
      message:
        listedCount > 0
          ? `This will set ${selected.length} selected part${selected.length === 1 ? "" : "s"} to ${preview != null ? PriceFormat(preview) : "the chosen price"}. ${listedCount} live eBay listing${listedCount === 1 ? "" : "s"} will update immediately. Unlisted stock only changes the entered price.`
          : `This will set ${selected.length} selected part${selected.length === 1 ? "" : "s"} to ${preview != null ? PriceFormat(preview) : "the chosen price"}. Unlisted stock only changes the entered price — nothing is listed on eBay.`,
      confirmButtonText: "Align prices",
    };
  };

  const handleConfirm = () => {
    if (confirmKind === "intelligent") {
      onAlign(selectedIds, PricingAlignTarget.Intelligent);
    } else if (confirmKind === "markdown") {
      onAlign(selectedIds, PricingAlignTarget.GradeMarkdown);
    } else if (confirmKind === "lastSold") {
      onAlign(selectedIds, PricingAlignTarget.LastSold);
    } else if (confirmKind === "lastSoldMarkdown") {
      onAlign(selectedIds, PricingAlignTarget.LastSoldGradeMarkdown);
    } else {
      onAlign(selectedIds, target, target === PricingAlignTarget.Custom ? parsedCustom : null);
    }
    setConfirmKind(null);
  };

  const handleBulkIntelligence = async () => {
    if (selected.length === 0) {
      addNotification({ primaryText: "Select at least one part", variant: "warn" });
      return;
    }

    try {
      const response = await intelligencePricingBulk.mutateAsync(selectedIds);
      const results = response?.data ?? [];
      const succeeded = results.filter((item) => (item.results ?? []).length > 0).length;
      const failed = results.length - succeeded;
      await queryClient.invalidateQueries(["pricing"]);
      await queryClient.invalidateQueries(["intelligenceReview"]);
      addNotification({
        primaryText: `Intelligence updated for ${succeeded} part${succeeded === 1 ? "" : "s"}${failed > 0 ? `, ${failed} returned no data` : ""}`,
        variant: failed > 0 && succeeded === 0 ? "warn" : "success",
      });
    } catch {
      addNotification({ primaryText: "Intelligence pricing failed", variant: "error" });
    }
  };

  const copy = confirmCopy();

  return (
    <section aria-label="Parts in the selected group" className="flex min-h-0 min-w-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-none flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">
          {rows.length > 0 ? `${rows.length} part${rows.length === 1 ? "" : "s"} in this group` : "Parts in this group"}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <ButtonInput
            label="Get intelligent prices"
            onClick={handleBulkIntelligence}
            disabled={selected.length === 0}
            isLoading={intelligencePricingBulk.isLoading}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Set to intelligent"
            onClick={() => setConfirmKind("intelligent")}
            disabled={intelReady.length === 0 || isAligning}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Apply grade markdowns"
            onClick={() => setConfirmKind("markdown")}
            disabled={markdownReady.length === 0 || isAligning}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Set to last sold"
            onClick={() => setConfirmKind("lastSold")}
            disabled={lastSoldReady.length === 0 || isAligning}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Apply last sold markdowns"
            onClick={() => setConfirmKind("lastSoldMarkdown")}
            disabled={lastSoldMarkdownReady.length === 0 || isAligning}
            size="sm"
            classes=""
          />
          <div role="group" aria-label="Align to" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
            <button type="button" onClick={() => setTarget(PricingAlignTarget.Lowest)} aria-pressed={target === PricingAlignTarget.Lowest} className={segmentClasses(target === PricingAlignTarget.Lowest)}>
              Lowest
            </button>
            <button type="button" onClick={() => setTarget(PricingAlignTarget.Average)} aria-pressed={target === PricingAlignTarget.Average} className={segmentClasses(target === PricingAlignTarget.Average)}>
              Average
            </button>
            <button type="button" onClick={() => setTarget(PricingAlignTarget.Custom)} aria-pressed={target === PricingAlignTarget.Custom} className={segmentClasses(target === PricingAlignTarget.Custom)}>
              Custom
            </button>
          </div>
          {target === PricingAlignTarget.Custom && (
            <input
              type="number"
              min={0.01}
              max={MAX_PART_PRICE}
              step={0.01}
              value={customPrice}
              onChange={(event) => setCustomPrice(event.target.value)}
              placeholder="Amount"
              className="h-8 w-24 rounded-md border-gray-300 text-sm"
            />
          )}
          <ButtonInput
            label={preview != null ? `Align ${selected.length} to ${PriceFormat(preview)}` : "Align selected"}
            onClick={() => setConfirmKind("align")}
            disabled={!canApply}
            size="sm"
            classes=""
          />
        </div>
      </div>

      {isLoading ? (
        <div className="px-4 py-6">
          <LoadingPulse />
        </div>
      ) : rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">No listed or in-stock parts match this group.</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={allSelected} onChange={toggleAll} aria-label="Select all parts" />
                </th>
                <th className="px-3 py-2">Part</th>
                <th className="px-3 py-2">Grade</th>
                <th className="px-3 py-2">Part numbers</th>
                <th className="px-3 py-2">Vehicle</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Days on eBay</th>
                <th className="px-3 py-2 text-right">Days in stock</th>
                <th className="px-3 py-2">Comment</th>
                <th className="px-3 py-2">eBay sold</th>
                <th className="px-3 py-2 text-right">Intelligent</th>
                <th className="px-3 py-2 text-right">Last sold</th>
                <th className="px-3 py-2 text-right">Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const numbers = partNumberLines(row);
                const markdown = hasGrade(row.grade) ? applyGradeMarkdown(row.intelligenceAverage, row.grade, gradeB, gradeC) : null;
                const lastSoldMarkdown = hasGrade(row.grade) ? applyGradeMarkdown(row.lastSoldPrice, row.grade, gradeB, gradeC) : null;

                return (
                  <tr key={row.partId} className={classNames(selectedIds.includes(row.partId) ? "bg-primary-50" : "hover:bg-gray-50")}>
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={selectedIds.includes(row.partId)}
                        onChange={() => toggle(row.partId)}
                        aria-label={`Select ${row.tagNumber ?? row.title ?? "part"}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {row.photoUrl ? (
                          <img src={row.photoUrl} alt="" className="h-10 w-10 rounded object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gray-100" />
                        )}
                        <div>
                          <Link to={`/stock/${row.partId}`} className="font-medium text-primary-700 hover:text-primary-800">
                            {row.tagNumber || "Open part"}
                          </Link>
                          {row.title && <div className="max-w-xs truncate text-xs text-gray-500">{row.title}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-800">{row.grade || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0">
                          {numbers.length === 0 ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            numbers.map((line) => (
                              <div key={line.label} className="text-xs text-gray-800">
                                <span className="font-medium text-gray-500">{line.label} </span>
                                {line.value}
                              </div>
                            ))
                          )}
                        </div>
                        <PricingPartProButton member={row} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {vehicleLabel(row)}
                      {row.categoryName && <div className="text-xs text-gray-500">{row.categoryName}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill
                        text={row.kind === PricingMemberKind.Listed ? "On eBay" : "In stock"}
                        colour={row.kind === PricingMemberKind.Listed ? StatusPillColour.Blue : StatusPillColour.Green}
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800">
                      {row.kind === PricingMemberKind.Listed ? formatDays(row.listedOn) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800">{formatDays(row.created)}</td>
                    <td className="px-3 py-2">
                      {row.internalComment ? (
                        <div className="max-w-[12rem] truncate text-xs text-gray-600" title={row.internalComment}>
                          {row.internalComment}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <UnlistedEbaySearchButtons part={ebaySearchPartFromMember(row)} compact />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      <div className="flex flex-col items-end gap-1">
                        <span>{row.intelligenceAverage != null ? PriceFormat(row.intelligenceAverage) : "—"}</span>
                        {markdown != null && markdown !== row.intelligenceAverage && (
                          <div className="text-xs text-gray-500">{PriceFormat(markdown)} after grade</div>
                        )}
                        {row.lastIntelligenceLookup && (
                          <div className="text-xs text-gray-500">{formatDateTime(row.lastIntelligenceLookup, DateTime.DATE_SHORT)}</div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setReviewPartId(row.partId);
                            setReviewOpen(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                        >
                          <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          View results
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      {row.lastSoldPrice != null && row.lastSoldPrice > 0 ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <span>{PriceFormat(row.lastSoldPrice)}</span>
                          {row.lastSoldOn && (
                            <div className="text-xs text-gray-500">{formatDateTime(row.lastSoldOn, DateTime.DATE_SHORT)}</div>
                          )}
                          {lastSoldMarkdown != null && lastSoldMarkdown !== row.lastSoldPrice && (
                            <div className="text-xs text-gray-500">{PriceFormat(lastSoldMarkdown)} after grade</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">{row.currentPrice != null ? PriceFormat(row.currentPrice) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <IntelligenceReviewModal
        open={reviewOpen}
        setOpen={setReviewOpen}
        review={latestReview.data?.data}
        isLoading={latestReview.isLoading && latestReview.fetchStatus !== "idle"}
        isError={latestReview.isError}
      />

      <ConfirmModal
        open={confirmKind != null}
        setOpen={(open: boolean) => {
          if (!open) setConfirmKind(null);
        }}
        title={copy.title}
        message={copy.message}
        confirmButtonText={copy.confirmButtonText}
        isLoading={isAligning}
        onConfirm={handleConfirm}
      />
    </section>
  );
}
